import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import RangePicker from "@/components/RangePicker";
import { adminApi } from "@/lib/api";
import { errText } from "@/lib/ranges";
import { tooltipPercentFormatter, legendPercentFormatter } from "@/lib/charts";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

// Same palette as the Dashboard (Pass 15) so the two views read as one
// system rather than two different products.
const CHART_COLORS = ["#3D3D3D", "#B8860B", "#2F5E3D", "#7A2E2E", "#4A6FA5"];

// Money is rendered whole — a revenue comparison across channels is a
// six-figure conversation and the sen are noise at that scale.
const myr = (n) => `MYR ${Math.round(Number(n) || 0).toLocaleString("en-MY")}`;

// Pass 59: spend, attribution and conversion tracking live on their own
// pages, so this one loads only lead data.
function SubpageLink({ to, title, body, testId }) {
  return (
    <Link to={to} className="scw-card" data-testid={testId}
      style={{ display: "block", padding: "20px 22px", textDecoration: "none", color: "inherit", flex: "1 1 260px", maxWidth: 380 }}>
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>{title} {"\u2192"}</p>
      <p style={{ fontSize: 13, margin: 0, color: "var(--scw-muted)", lineHeight: 1.6 }}>{body}</p>
    </Link>
  );
}

export default function AdminAnalytics() {
  const [range, setRange] = useState(null); // {from, to} — set once the first response arrives
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (params) => {
    setErr("");
    setLoading(true);
    try {
      const r = await adminApi.analytics(params || {});
      setData(r.data);
      setRange(r.data.range);
    } catch (e) {
      setErr(errText(e, "Failed to load."));
    } finally {
      setLoading(false);
    }
  }, []);

  // Load once on mount with no params — the backend defaults to "this
  // month" (same window the Dashboard shows) and returns available_months
  // so the picker below doesn't have to guess how far back leads go.
  useEffect(() => { load(); }, [load]);

  const onRangeChange = (next) => {
    setRange(next);
    load(next);
  };

  if (loading && !data) return <AdminLayout>{"Loading…"}</AdminLayout>;
  if (err && !data) {
    return (
      <AdminLayout>
        <div style={{ padding: 24, border: "1px solid var(--scw-line)", background: "#fcf8f4" }} data-testid="analytics-load-error">
          <p className="scw-eyebrow" style={{ marginBottom: 6, color: "#a13b3b" }}>Analytics temporarily unavailable</p>
          <p style={{ margin: "0 0 12px 0", fontSize: 13 }}>{err}</p>
          <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "6px 14px" }} onClick={() => load()} data-testid="analytics-retry-btn">
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Historical performance</p>
      <h1 style={{ fontSize: 28, margin: "0 0 28px 0" }}>Analytics</h1>

      <RangePicker range={range} months={data.available_months} loading={loading} onChange={onRangeChange} />
      {err && <p style={{ fontSize: 12, color: "#a13b3b", margin: "-12px 0 16px 0" }} data-testid="analytics-range-error">{err}</p>}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 32, alignItems: "stretch" }}>
        <div className="scw-card" style={{ padding: "24px 24px", flex: "0 0 220px" }}>
          <p className="scw-eyebrow" style={{ marginBottom: 10 }}>Leads in range</p>
          <p style={{ fontSize: 36, margin: 0, fontWeight: 500 }} data-testid="analytics-total">{data.total_leads}</p>
        </div>
        <SubpageLink to="/admin/analytics/marketing" testId="analytics-link-marketing"
          title="Marketing & attribution"
          body="Spend against return by channel, monthly spend entry, attribution capture." />
        <SubpageLink to="/admin/analytics/capi" testId="analytics-link-capi"
          title="Conversion tracking"
          body="Qualified and won leads sent to Google and Meta, duplicate checks, send log." />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 32 }}>
        <ErrorBoundary label="Leads by type chart">
          <div className="scw-card" data-testid="analytics-chart-type">
            <p className="scw-eyebrow" style={{ marginBottom: 12 }}>Leads by type</p>
            {(data.by_type || []).every((d) => d.value === 0) ? (
              <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No data in this range.</p>
            ) : (
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_type || []}>
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#5C5C5C" }} axisLine={{ stroke: "#eaeaea" }} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#9a9a9a" }} axisLine={{ stroke: "#eaeaea" }} tickLine={false} />
                    <Tooltip cursor={{ fill: "rgba(92,92,92,0.06)" }} formatter={tooltipPercentFormatter(data.by_type || [])} />
                    <Bar dataKey="value">
                      {(data.by_type || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </ErrorBoundary>
        <ErrorBoundary label="Leads by status chart">
          <div className="scw-card" data-testid="analytics-chart-status">
            <p className="scw-eyebrow" style={{ marginBottom: 12 }}>Leads by status</p>
            {(data.by_status || []).every((d) => d.value === 0) ? (
              <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No data in this range.</p>
            ) : (
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.by_status} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
                      {(data.by_status || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={tooltipPercentFormatter(data.by_status || [])} />
                    <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 10 }} formatter={legendPercentFormatter(data.by_status || [])} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </ErrorBoundary>
        <ErrorBoundary label="Leads by source chart">
          <div className="scw-card" data-testid="analytics-chart-source">
            <p className="scw-eyebrow" style={{ marginBottom: 12 }}>Leads by source</p>
            {!(data.by_source || []).length ? (
              <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No data in this range.</p>
            ) : (
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.by_source} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
                      {(data.by_source || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={tooltipPercentFormatter(data.by_source || [])} />
                    <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 10 }} formatter={legendPercentFormatter(data.by_source || [])} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </ErrorBoundary>
        <ErrorBoundary label="Leads by marketing source chart">
          <div className="scw-card" data-testid="analytics-chart-utm-source">
            <p className="scw-eyebrow" style={{ marginBottom: 4 }}>Leads by marketing channel</p>
            <p style={{ fontSize: 11, color: "var(--scw-muted)", marginBottom: 12 }}>
              Same channel rules as the Marketing page.
            </p>
            {!(data.by_channel || []).length ? (
              <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No data in this range.</p>
            ) : (
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.by_channel} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
                      {(data.by_channel || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={tooltipPercentFormatter(data.by_channel || [])} />
                    <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 10 }} formatter={legendPercentFormatter(data.by_channel || [])} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </ErrorBoundary>
      </div>

      <h2 style={{ fontSize: 18, margin: "32px 0 16px 0" }}>Revenue by marketing channel</h2>
      <ErrorBoundary label="Revenue by channel">
        <div className="scw-card" style={{ padding: 0, marginBottom: 32 }} data-testid="analytics-revenue-by-source">
          <p style={{ fontSize: 11, color: "var(--scw-muted)", padding: "16px 18px 0 18px", margin: 0, lineHeight: 1.6 }}>
            Attributed by the date the lead arrived, not the date it closed, so revenue lines up against the spend that produced it.
            A recent range therefore understates until its deals close.
          </p>
          {!(data.revenue_by_channel || []).length ? (
            <p style={{ color: "var(--scw-muted)", fontSize: 13, padding: 18 }}>No data in this range.</p>
          ) : (
            <div className="scw-table-wrap">
              <table className="scw-table">
                <thead><tr>
                  <th>Channel</th><th>Leads</th><th>Won</th><th>Revenue</th><th>Value per lead</th>
                </tr></thead>
                <tbody>
                  {(data.revenue_by_channel || []).map((r) => (
                    <tr key={r.label} data-testid={`analytics-revenue-row-${r.label}`}>
                      <td>{r.label}</td>
                      <td>{r.leads}</td>
                      <td>{r.won}</td>
                      <td>{myr(r.revenue)}</td>
                      {/* The column that actually decides where spend goes:
                          a channel with fewer, richer leads beats a cheap
                          one, and only this ratio shows it. */}
                      <td style={{ fontWeight: 500 }}>{myr(r.value_per_lead)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </ErrorBoundary>

      <h2 style={{ fontSize: 18, margin: "32px 0 16px 0" }}>First response time</h2>
      <ErrorBoundary label="Response time">
        <div className="scw-card" style={{ marginBottom: 32 }} data-testid="analytics-response-time">
          {!(data.response_time?.count) ? (
            <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No measurable leads in this range.</p>
          ) : (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 32, marginBottom: 8 }}>
                <div>
                  <p className="scw-eyebrow" style={{ marginBottom: 6 }}>Median</p>
                  <p style={{ fontSize: 30, margin: 0, fontWeight: 500 }} data-testid="analytics-response-median">
                    {data.response_time.median_hours}h
                  </p>
                </div>
                <div>
                  <p className="scw-eyebrow" style={{ marginBottom: 6 }}>Slowest 10%</p>
                  <p style={{ fontSize: 30, margin: 0, fontWeight: 500 }}>{data.response_time.p90_hours}h</p>
                </div>
                <div>
                  <p className="scw-eyebrow" style={{ marginBottom: 6 }}>Met SLA</p>
                  <p style={{ fontSize: 30, margin: 0, fontWeight: 500 }} data-testid="analytics-response-sla">
                    {data.response_time.within_sla_pct}%
                  </p>
                </div>
                <div>
                  <p className="scw-eyebrow" style={{ marginBottom: 6 }}>Measured</p>
                  <p style={{ fontSize: 30, margin: 0, fontWeight: 500 }}>{data.response_time.count}</p>
                </div>
              </div>
              <p style={{ fontSize: 11, color: "var(--scw-muted)", marginBottom: 18, lineHeight: 1.6 }}>
                Time from a lead arriving to a specialist first moving it out of New, derived from each lead&apos;s status history.
                SLA is first contact by the end of the next opening day. Walk-ins and manually logged leads are excluded — the
                conversation had already happened, so there was no waiting customer to measure.
              </p>
              {(data.response_time.trend || []).length > 1 && (
                <div style={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.response_time.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eaeaea" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} unit="h" />
                      <Tooltip formatter={(v) => [`${v}h`, "Median"]} />
                      <Line type="monotone" dataKey="median_hours" stroke={CHART_COLORS[1]} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </div>
      </ErrorBoundary>

      <h2 style={{ fontSize: 18, margin: "32px 0 16px 0" }}>Why deals were lost</h2>
      <ErrorBoundary label="Lost reasons">
        <div className="scw-card" style={{ marginBottom: 32 }} data-testid="analytics-lost-reasons">
          {!(data.lost_reasons || []).length ? (
            <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No closed-lost leads in this range.</p>
          ) : (
            <>
              <p style={{ fontSize: 11, color: "var(--scw-muted)", marginBottom: 12, lineHeight: 1.6 }}>
                Losing on price, on stock, and on speed are three different problems. Deals closed before this was captured show as
                &quot;Not recorded&quot;.
              </p>
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.lost_reasons} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
                      {(data.lost_reasons || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={tooltipPercentFormatter(data.lost_reasons || [])} />
                    <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 10 }} formatter={legendPercentFormatter(data.lost_reasons || [])} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      </ErrorBoundary>

    </AdminLayout>
  );
}
